import React, { useState, useEffect } from 'react';
import { Box, Typography, Chip, CircularProgress } from '@mui/material';
import api from '../services/api';

const TagSuggestions = ({ itemName, itemDescription, onAddTag }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedTags, setSelectedTags] = useState([]);

  useEffect(() => {
    // Only fetch suggestions if we have some content to base them on
    if (!itemName && !itemDescription) {
      setSuggestions([]);
      return;
    }

    const fetchSuggestions = async () => {
      setLoading(true);
      try {
        const response = await api.post(
          '/tags/suggest',
          { name: itemName, description: itemDescription }
        );
        setSuggestions(response.data.suggestions || []);
        setError(null);
      } catch (err) {
        console.error('Error fetching tag suggestions:', err);
        setError('Failed to get tag suggestions');
      } finally {
        setLoading(false);
      }
    };

    // Debounce to avoid too many API calls
    const timerId = setTimeout(() => {
      fetchSuggestions();
    }, 500);

    return () => clearTimeout(timerId);
  }, [itemName, itemDescription]);

  const handleTagClick = (tag) => {
    // Add to selected tags
    if (!selectedTags.includes(tag)) {
      setSelectedTags([...selectedTags, tag]);
      // Call parent callback
      if (onAddTag) {
        onAddTag(tag);
      }
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', my: 1 }}>
        <CircularProgress size={20} sx={{ mr: 1 }} />
        <Typography variant="body2" color="text.secondary">
          Finding relevant tags...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ my: 1 }}>
        <Typography variant="body2" color="error" fontSize="0.8rem">
          {error}
        </Typography>
      </Box>
    );
  }

  if (suggestions.length === 0) {
    return null;
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Suggested Tags:
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {suggestions.map((tag) => (
          <Chip
            key={tag}
            label={tag}
            size="small"
            color={selectedTags.includes(tag) ? "primary" : "default"}
            onClick={() => handleTagClick(tag)}
            sx={{
              opacity: selectedTags.includes(tag) ? 0.6 : 1,
              '&:hover': {
                backgroundColor: selectedTags.includes(tag) ? undefined : '#e0f7fa'
              }
            }}
          />
        ))}
      </Box>
    </Box>
  );
};

export default TagSuggestions;
